import _ from 'lodash';
import { Observable } from 'rxjs';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';
import { replace, push } from 'react-router-redux';

import DonationMatchingRulesApi from 'entities/donationMatchingRule/api';
import BudgetApi from 'entities/budget/api';
import Budget from 'entities/budget/model';
import LocationsApi from 'entities/location/api';
import { selectBudgetById } from 'entities/budget/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { selectDonationMatchingRules } from 'entities/donationMatchingRule/selectors';
import { selectUser, selectUserLocations } from 'entities/user/selectors';
import { Features } from 'utils/features';

import {
  initSuccessAction,
  initFailedAction,
  saveSuccessAction,
  saveFailedAction,
  removeSuccessAction,
  removeFailedAction,
} from './actions';
import { FORM_IDENTIFIER, INIT, SAVE, REMOVE } from './constants';

const init = (action$, store) =>
  action$.ofType(INIT).mergeMap(({ id }) => {
    const state = store.getState();
    const actions = [];

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationsApi.fetchAll());
    }

    if (id === 'new') {
      let defaultValues;
      const user = selectUser(state);
      if (user?.hasFeature(Features.BUDGET_LIMIT_ASSERTION_TYPE)) {
        defaultValues = { limitAssertionType: 'Contribution' };
      }
      actions.push(Observable.of(initialize(FORM_IDENTIFIER, new Budget(defaultValues))));
    } else {
      const budget = selectBudgetById(state, id);
      if (budget) {
        actions.push(Observable.of(initialize(FORM_IDENTIFIER, budget)));
      } else {
        actions.push(
          BudgetApi.fetch(id)
            .map((action) => [action, initialize(FORM_IDENTIFIER, action.budget)])
            .catch((e) => Observable.of(initFailedAction(e), replace('/notFound')))
        );
      }

      const donationMatchingRules = selectDonationMatchingRules(state);
      if (donationMatchingRules.size === 0) {
        actions.push(DonationMatchingRulesApi.fetchAll());
      }
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$) =>
  action$.ofType(SAVE).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.from(
        payload.data.id
          ? BudgetApi.update(payload.data.id, payload.data)
          : BudgetApi.create(payload.data)
      )
        .mergeMap((action) => [
          action,
          saveSuccessAction(),
          stopSubmit(FORM_IDENTIFIER),
          initialize(FORM_IDENTIFIER, action.budget.toJS()),
          push('/donations/rules'),
          showMessageAction(payload.data.id ? 'Changes Saved!' : 'Giving Budget Created!'),
        ])
        .catch((e) =>
          Observable.of(
            saveFailedAction(e, 'saveFailed'),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('An error occurred during saving, please try again.')
          )
        )
    )
  );

const remove = (action$) =>
  action$.ofType(REMOVE).mergeMap((payload) =>
    Observable.from(BudgetApi.delete(payload.budgetId))
      .mergeMap((action) => [
        action,
        removeSuccessAction(),
        push('/donations/rules'),
        showMessageAction('Giving Budget Removed!'),
      ])
      .catch((e) =>
        Observable.of(
          removeFailedAction(e, 'removeFailed'),
          stopSubmit(FORM_IDENTIFIER),
          showErrorAction('An error occurred during saving, please try again.')
        )
      )
  );

export default [init, save, remove];
